import React, { useRef, useEffect, useMemo, useCallback } from "react";
import MessageBubble from "./MessageBubble";
import QuickQuestions from "./QuickQuestions";
// import LoadingState from "./LoadingState";
import { createSkeletonMessage } from "../services/ChatService.js";

const MessageList = ({
  messages = [],
  isDark,
  primaryColor,
  isLoading,
  agentMessageBubbleColor,
  userMessageBoxColor,
  textColor,
  assistantTextColor,
  userTextColor,
  fontFamily,
  agentMessageBubbleFontFamily,
  agentMessageBubbleFontSize,
  userMessageBubbleFontFamily,
  userMessageBubbleFontSize,
  fontSize,
  formatTime,
  isRtl = false,
  quickQuestions = [],
  shouldShowQuickQuestions = false,
  onQuickQuestion,
  quickQuestionsLayout = "vertical",
  onDecision,
  disclaimerText,
  disclaimerPosition = "top",
  resolvedAgentBubbleColor,
  resolvedUserMessageColor,
  resolvedTextColor,
  isVoiceSessionActive = false,
  voiceStatus = "idle",
  messagesContainerRef,
  companyLogo,
  conciergeName,
  companyName,
}) => {
  const messagesEndRef = useRef(null);
  const shouldAutoScrollRef = useRef(true);

  const autoScrollKey = useMemo(
    () =>
      messages
        .map((message) => `${message.id}:${message.content?.length ?? 0}:${message.isProcessing ? 1 : 0}`)
        .join("|"),
    [messages]
  );

  const updateAutoScrollIntent = useCallback(() => {
    const container = messagesContainerRef?.current;
    if (!container) {
      return;
    }

    const distanceFromBottom = container.scrollHeight - container.scrollTop - container.clientHeight;
    shouldAutoScrollRef.current = distanceFromBottom < 160;
  }, [messagesContainerRef]);

  useEffect(() => {
    const container = messagesContainerRef?.current;
    if (!container) {
      return undefined;
    }

    container.addEventListener("scroll", updateAutoScrollIntent, { passive: true });
    updateAutoScrollIntent();

    return () => {
      container.removeEventListener("scroll", updateAutoScrollIntent);
    };
  }, [updateAutoScrollIntent, messagesContainerRef]);

  useEffect(() => {
    if (!shouldAutoScrollRef.current) {
      return undefined;
    }

    if (typeof window === "undefined") {
      return undefined;
    }

    const frame = window.requestAnimationFrame(() => {
      messagesEndRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "end",
      });
    });

    return () => {
      window.cancelAnimationFrame(frame);
    };
  }, [autoScrollKey, isLoading]);

  return (
    <>
      {/* Loading state for voice session */}
      {/* {isVoiceSessionActive && messages.length === 0 && (
        <div className="flex flex-col items-center justify-center h-full">
          <LoadingState
            title="Starting voice session..."
            description="Allow microphone access or begin speaking to continue."
            isAnimated={true}
          />
        </div>
      )} */}

      {/* Disclaimer at Top */}
      {disclaimerText && disclaimerPosition === "top" && (
        <div className="flex justify-center">
          <div
            className="rounded-lg px-2 py-1 max-w-[85%] border"
            style={{
              backgroundColor: agentMessageBubbleColor !== undefined && agentMessageBubbleColor !== null
                ? agentMessageBubbleColor
                : (resolvedAgentBubbleColor || "#F5F5F5"),
              color: assistantTextColor !== undefined && assistantTextColor !== null ? assistantTextColor : "#1A1A1A",
              fontFamily: fontFamily !== undefined && fontFamily !== null ? fontFamily : "Inter",
              fontSize: fontSize !== undefined && fontSize !== null ? `calc(${fontSize} - 2px)` : "12px",
              direction: isRtl ? "rtl" : "ltr",
              textAlign: "center",
              borderColor: isDark ? "#374151" : "#e5e7eb",
            }}
          >
            <p
              className="leading-relaxed whitespace-pre-wrap break-words"
              dir={isRtl ? "rtl" : "ltr"}
              style={{
                color: assistantTextColor !== undefined && assistantTextColor !== null ? assistantTextColor : "#1A1A1A",
                fontFamily: fontFamily !== undefined && fontFamily !== null ? fontFamily : "Inter",
                fontSize: fontSize !== undefined && fontSize !== null ? `calc(${fontSize} - 2px)` : "12px",
              }}
            >
              {disclaimerText}
            </p>
          </div>
        </div>
      )}

      {messages.map((message) => (
        <MessageBubble
          key={message.id}
          message={message}
          isDark={isDark}
          primaryColor={primaryColor}
          agentMessageBubbleColor={agentMessageBubbleColor}
          userMessageBoxColor={userMessageBoxColor}
          textColor={textColor}
          assistantTextColor={assistantTextColor}
          userTextColor={userTextColor}
          fontFamily={fontFamily}
          agentMessageBubbleFontFamily={agentMessageBubbleFontFamily}
          agentMessageBubbleFontSize={agentMessageBubbleFontSize}
          userMessageBubbleFontFamily={userMessageBubbleFontFamily}
          userMessageBubbleFontSize={userMessageBubbleFontSize}
          fontSize={fontSize}
          formatTime={formatTime}
          isRtl={isRtl}
          onDecision={onDecision}
          companyLogo={companyLogo}
          conciergeName={conciergeName}
          companyName={companyName}
        />
      ))}

      {/* Show typing indicator when waiting for first chunk (before skeleton is created) */}
      {(() => {
        const lastMessage = messages[messages.length - 1];
        const isWaitingForFirstChunk = isLoading && lastMessage?.role === 'user';
        const hasProcessingAssistantMessage = messages.some(
          msg => msg.role === 'assistant' && (msg.isProcessing || !msg.content)
        );

        if (isWaitingForFirstChunk && !hasProcessingAssistantMessage) {
          return (
            <MessageBubble
              key="skeleton-waiting"
              message={createSkeletonMessage()}
              isDark={isDark}
              primaryColor={primaryColor}
              agentMessageBubbleColor={agentMessageBubbleColor}
              userMessageBoxColor={userMessageBoxColor}
              textColor={textColor}
              assistantTextColor={assistantTextColor}
              userTextColor={userTextColor}
              fontFamily={fontFamily}
              agentMessageBubbleFontFamily={agentMessageBubbleFontFamily}
              agentMessageBubbleFontSize={agentMessageBubbleFontSize}
              userMessageBubbleFontFamily={userMessageBubbleFontFamily}
              userMessageBubbleFontSize={userMessageBubbleFontSize}
              fontSize={fontSize}
              formatTime={formatTime}
              isRtl={isRtl}
              onDecision={onDecision}
              companyLogo={companyLogo}
              conciergeName={conciergeName}
              companyName={companyName}
            />
          );
        }
        return null;
      })()}

      {shouldShowQuickQuestions && quickQuestions.length > 0 && (
        <div className="pt-1">
          <QuickQuestions
            questions={quickQuestions}
            isDark={isDark}
            primaryColor={primaryColor}
            onQuestionClick={onQuickQuestion}
            isTyping={isLoading}
            layout={quickQuestionsLayout}
            fontFamily={fontFamily}
            fontSize={fontSize}
            textColor={textColor}
            isRtl={isRtl}
            questionTextColor={assistantTextColor}
            questionBoxColor={agentMessageBubbleColor}
          />
        </div>
      )}

      <div ref={messagesEndRef} className="h-px" />
    </>
  );
};

export default MessageList;
